import React, { useState } from "react"
import MeetingBadge from "../layout/MeetingBadge"
import LangSelector from "../layout/LangSelector"
import { Trans, useLingui } from "@lingui/react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import PropTypes from "prop-types"
import { useSelector, useDispatch } from "react-redux"
import {
  Collapse,
  Navbar,
  NavbarToggler,
  NavbarBrand,
  Nav,
  NavLink as Nav2,
  NavItem,
  UncontrolledDropdown,
  DropdownMenu,
  DropdownItem,
} from "reactstrap"
import { setCurrentPage } from "../../store/reducers/navigation"
import { navigationMap } from "../../view/shared/helpers"
import { logoutAction } from "../../store/actions/auth"
import StatusBanner from "./NetworkStatusBanner"
import { selectCycle } from "../../store/actions/meetings"
import styles from "./Header.module.css"

const CollapsableMenu = ({
  isOpen,
  toggle,
  setPage,
  logOut,
}) => {
  const links = [
    {
      href: "https://hiveonlineplanning.atlassian.net/wiki/x/AYCzgQ",
      id: "header.whats_new",
      genLabel: () => <Trans id="header.whats_new"/>,
    },
    {
      href: "https://hiveonlineplanning.atlassian.net/wiki/spaces/AT/overview#VSLA",
      id: "header.tutorials",
      genLabel: () => <Trans id="header.tutorials"/>
    },
    {
      href: "https://hiveonlineplanning.atlassian.net/l/cp/skb5xbq7",
      id: "header.legal",
      genLabel: () => <Trans id="header.legal"/>
    },
  ]
  const { i18n } = useLingui()
  const [languageSelectorIsOpen, setLanguageSelectorOpen] = useState(false)

  const logoutAndResetNavState = () => {
    toggle()
    logOut()
  }

  return (
    <Collapse
      isOpen={isOpen}
      navbar
      className={styles.collapse}
      style={{
        backgroundColor: '#f8f9fa',
        textAlign: i18n.locale === "ar" ? "right" : "left",
      }}
    >
      <Nav className="mr-auto" navbar>
        <NavItem>
          <Nav2
            className={styles.navLink}
            onClick={() => {
              setPage("group-main")
              toggle()
            }}
          >
            <span className={styles.span}>
              <Trans id="Home" />
            </span>
          </Nav2>
        </NavItem>
        <NavItem onClick={toggle}>
          <MeetingBadge />
        </NavItem>
        <UncontrolledDropdown nav inNavbar>
          <span
            type="button"
            style={{ display: "flex", alignItems: "center" }}
            onClick={() => setLanguageSelectorOpen(!languageSelectorIsOpen)}
          >
            <span className={styles.span} style={{ color: "initial" }}>
              <Trans id="language" />
            </span>
            <FontAwesomeIcon
              style={{ padding: "0 4px", fontSize: "35px" }}
              icon="angle-right"
            />
          </span>
          {languageSelectorIsOpen && <LangSelector toggleBack={toggle} />}
          <DropdownMenu className={`shadow p-3 mb-5 rounded ${styles.dropdownMenu}`}>
            <DropdownItem divider />
            <DropdownItem onClick={toggle}>
              <span className={styles.span}>
                <Trans id="close" />
              </span>
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
        {links.map((link) => (
          <a
            key={link.id}
            className={styles.navLink}
            style={{ padding: "0.5rem 0", textDecoration: "none" }}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className={styles.span}>
              {link.genLabel()}
            </span>
          </a>
        ))}
        <Nav2
          className={styles.navLink}
          onClick={logoutAndResetNavState}
        >
          <span className={styles.span}>
            <Trans id="logout" />
          </span>
        </Nav2>
      </Nav>
    </Collapse>
  )
}

CollapsableMenu.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  setPage: PropTypes.func,
  logOut: PropTypes.func,
}

const Header = () => {
  const { i18n } = useLingui()
  const [isOpen, setIsOpen] = useState(false)
  const dispatch = useDispatch()

  const { selectedCycle, currentPage, user } = useSelector((state) => ({
    selectedCycle: state.cycles.selectedCycle,
    currentPage: state.currentPage,
    user: state.user,
  }))

  const toggle = () => setIsOpen(!isOpen)

  const setActivePage = (page) => dispatch(setCurrentPage(page))
  const logMeOut = () => dispatch(logoutAction())
  const setCycle = (item) => dispatch(selectCycle(item))

  const handleSetPage = () => {
    const metadata = currentPage.split("-")
    const pages = navigationMap[metadata[0]]
    const pageInfo = pages[metadata[1]]

    if (pageInfo.backPage === "meetingsMenu" && selectedCycle) {
      setCycle(null)
      return
    }
    if (!currentPage || !pageInfo.backPage) {
      window.history.push("/")
    }
    setActivePage(`${metadata[0]}-${pageInfo.backPage}`)
  }

  const isUatOrStaging = ["uat", "staging"].includes(
    window.REACT_APP_PWA_APP_ENV.toLocaleLowerCase(),
  )
  const navbarStyle = {
    backgroundColor: isUatOrStaging ? "#dc2f02" : "#fff",
    flexDirection: i18n.locale === "ar" ? "row-reverse" : "initial",
  }

  const isBackNav = currentPage && currentPage.split("-")[1] !== "main"
  const token = user ? user.token || localStorage.getItem("token") : null

  return (
    <div className={styles.navbar}>
      <Navbar light expand="md" style={navbarStyle}>
        <StatusBanner />
        <NavbarBrand
          className={`${styles.navbarBrand} ${isBackNav ? "arrow-icon" : "no-arrow-icon"
            }`}
        >
          {isBackNav ? (
            <>
              <FontAwesomeIcon
                onClick={handleSetPage}
                className={styles.icon}
                icon="arrow-left"
              />
              <span className={styles.navbarBrandTitle}>vsla.online</span>
              {isUatOrStaging && (
                <span className={styles.envText}>
                  {window.REACT_APP_PWA_APP_ENV}
                </span>
              )}
              <NavbarToggler
                className={styles.icon}
                onClick={toggle} />
            </>
          ) : (
            <>
              vsla.online
              {isUatOrStaging && (
                <span className={styles.envText}>
                  {window.REACT_APP_PWA_APP_ENV}
                </span>
              )}
            </>
          )}
        </NavbarBrand>
        {!isBackNav && user && user.token && <NavbarToggler onClick={toggle} />}
      </Navbar>
      {token && (
        <CollapsableMenu
          isOpen={isOpen}
          toggle={toggle}
          setPage={setActivePage}
          logOut={logMeOut}
        />
      )}
    </div>
  )
}

export default Header
